import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LoginResponse } from '@pokerroom/types';

interface AuthState {
  accessToken: string | null;
  authRevision: number;
  /** Login/logout boundary; refresh keeps same-venue form drafts intact. */
  sessionRevision: number;
  user: LoginResponse['user'] | null;
  isAuthenticated: boolean;
  visiblePages: string[] | null;
  allowedActions: string[] | null;

  setAuth: (data: LoginResponse) => void;
  clearAuth: () => void;
  updateAccessToken: (token: string) => void;
  setPermissions: (pages: string[], actions: string[]) => void;
}

/**
 * 認證狀態 Store
 * Access Token 存 memory，不寫 localStorage（防 XSS）
 * 使用者基本資訊 persist 至 sessionStorage（便利性）
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      authRevision: 0,
      sessionRevision: 0,
      user: null,
      isAuthenticated: false,
      visiblePages: null,
      allowedActions: null,

      setAuth: (data: LoginResponse) =>
        set((state) => ({
          authRevision: state.authRevision + 1,
          sessionRevision: state.sessionRevision + 1,
          accessToken: data.accessToken,
          user: data.user,
          isAuthenticated: true,
        })),

      clearAuth: () =>
        set((state) => ({
          authRevision: state.authRevision + 1,
          sessionRevision: state.sessionRevision + 1,
          accessToken: null,
          user: null,
          isAuthenticated: false,
          visiblePages: null,
          allowedActions: null,
        })),

      updateAccessToken: (token: string) =>
        set((state) => ({ accessToken: token, authRevision: state.authRevision + 1 })),

      setPermissions: (pages: string[], actions: string[]) =>
        set({ visiblePages: pages, allowedActions: actions }),
    }),
    {
      name: 'pokerroom-auth',
      storage: {
        getItem: (key) => {
          try {
            const value = localStorage.getItem(key);
            return value ? JSON.parse(value) : null;
          } catch {
            console.warn('[auth.store] Failed to parse localStorage, resetting');
            localStorage.removeItem(key);
            return null;
          }
        },
        setItem: (key, value) => {
          localStorage.setItem(key, JSON.stringify(value));
        },
        removeItem: (key) => {
          localStorage.removeItem(key);
        },
      },
      // 只持久化使用者資訊，不持久化 token（token 在記憶體，重載後需重新整理）
      // 使用 localStorage 以便 Playwright storageState 可還原 E2E 登入狀態
      partialize: (state) =>
        ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          visiblePages: state.visiblePages,
          allowedActions: state.allowedActions,
        }) as AuthState,
    },
  ),
);

/** Untrusted display/cache partition only; never use this as authorization. */
export function sessionVenueKey(accessToken: string | null): string {
  try {
    const payload = (accessToken?.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
    const venueId: unknown = JSON.parse(atob(payload)).venueId;
    return typeof venueId === 'string' ? venueId : '';
  } catch {
    return '';
  }
}
